import { Button, cn } from '@repo/ui'
import { AlertTriangle, Check, Circle, Loader2, RotateCcw, TriangleAlert } from 'lucide-react'
import type { AgentRun } from '@/api/proposals/proposals'

export function isActiveBuildRun(run: AgentRun): boolean {
  return run.status === 'queued' || run.status === 'running' || run.status === 'waiting_for_input'
}

function proposalStageLabel(run: AgentRun): string {
  if (run.status === 'waiting_for_input') return 'Waiting for input'
  const labels: Record<string, string> = {
    'proposal.validate': 'Preparing proposal',
    'proposal.parse_template': 'Reading template',
    'proposal.validate_corpus': 'Checking source content',
    'proposal.generate_section': 'Drafting sections',
    'proposal.finalize': 'Finishing proposal'
  }
  return labels[run.currentStage] ?? 'Building proposal'
}

export function ProposalBuildProgress({
  run,
  failureMessage,
  stopping,
  retrying,
  retryDisabled,
  onStop,
  onRetry,
  compact = false
}: {
  run: AgentRun
  failureMessage: string | null
  stopping: boolean
  retrying: boolean
  retryDisabled: boolean
  onStop: () => void
  onRetry: () => void
  compact?: boolean
}) {
  const active = isActiveBuildRun(run)
  const sectionSteps = run.steps.filter((step) => step.sectionId !== null)
  const progressLabel = run.totalStepsFinal
    ? `${run.completedSteps} of ${run.totalSteps ?? 0} steps complete`
    : run.status === 'failed'
      ? 'Build stopped before the section plan was complete'
      : 'Preparing the section plan'

  return (
    <section
      aria-labelledby="proposal-build-progress-title"
      aria-live="polite"
      className={
        compact
          ? 'min-w-0 border-b pb-4'
          : 'flex max-h-[min(20rem,40vh)] shrink-0 items-start justify-center overflow-y-auto'
      }
    >
      <div className={compact ? 'min-w-0' : 'w-full max-w-xl rounded-lg border bg-card p-5 shadow-sm sm:p-6'}>
        <div
          className={
            compact
              ? 'flex items-start justify-between gap-4'
              : 'flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'
          }
        >
          <div className="min-w-0">
            <h2 id="proposal-build-progress-title" className={cn('font-semibold', compact ? 'text-sm' : 'text-lg')}>
              {run.status === 'failed' ? 'Build needs attention' : 'Building proposal'}
            </h2>
            <p className="mt-1 text-sm font-medium text-foreground">{proposalStageLabel(run)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{progressLabel}</p>
          </div>
          {active ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onStop}
              disabled={stopping || run.cancelRequested}
              aria-label={stopping || run.cancelRequested ? 'Stopping build' : 'Stop build'}
              className="shrink-0"
            >
              {stopping || run.cancelRequested ? (
                <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
              ) : null}
              {stopping || run.cancelRequested ? 'Stopping...' : 'Stop'}
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={onRetry} disabled={retryDisabled} aria-label="Retry build">
              <RotateCcw className={retrying ? 'size-4 animate-spin motion-reduce:animate-none' : 'size-4'} />
              {retrying ? 'Retrying...' : 'Retry'}
            </Button>
          )}
        </div>

        {run.status === 'failed' && failureMessage && (
          <div
            role="alert"
            className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {failureMessage}
          </div>
        )}

        {sectionSteps.length > 0 ? (
          <details open={compact ? undefined : true} className={compact ? 'mt-3' : 'mt-6'}>
            <summary
              className={
                compact
                  ? 'cursor-pointer rounded-sm text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
                  : 'hidden'
              }
            >
              Section progress ({sectionSteps.length})
            </summary>
            <ol className={compact ? 'mt-2 space-y-1' : 'space-y-1'} aria-label="Proposal section progress">
              {sectionSteps.map((step) => {
                const isCurrent =
                  step.status === 'running' ||
                  step.status === 'waiting_for_input' ||
                  (step.status === 'pending' && step.sectionId === run.currentSectionId)
                const failed = step.status === 'failed'
                return (
                  <li key={step.stepKey} className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-3 py-2 text-sm">
                    {step.status === 'succeeded' ? (
                      <span className="flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check aria-hidden="true" className="size-3.5" />
                      </span>
                    ) : isCurrent ? (
                      <Loader2
                        aria-hidden="true"
                        className="size-5 animate-spin text-primary motion-reduce:animate-none"
                      />
                    ) : failed ? (
                      <TriangleAlert aria-hidden="true" className="size-5 text-destructive" />
                    ) : (
                      <Circle aria-hidden="true" className="size-5 text-muted-foreground/50" />
                    )}
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <span
                          className={cn('break-words', isCurrent ? 'font-medium text-foreground' : 'text-foreground')}
                        >
                          {step.heading || 'Proposal section'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {step.status === 'succeeded'
                            ? 'Complete'
                            : isCurrent
                              ? 'In progress'
                              : failed
                                ? 'Needs attention'
                                : 'Waiting'}
                        </span>
                      </div>
                      {step.warnings.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {step.warnings.map((warning, index) => (
                            <div
                              key={`${step.stepKey}-${index}`}
                              role="status"
                              className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-2 text-xs text-warning"
                            >
                              <TriangleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-warning" />
                              <span>{warning}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          </details>
        ) : run.status === 'failed' ? (
          <div className="mt-6 flex items-start gap-3 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-destructive" />
            No section progress is available because the build stopped during this stage.
          </div>
        ) : (
          <div className="mt-6 flex items-center gap-3 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
            Sections will appear here as the template is prepared.
          </div>
        )}
      </div>
    </section>
  )
}

const presentationBuildStages = [
  { key: 'presentation.validate', label: 'Preparing presentation' },
  { key: 'presentation.validate_corpus', label: 'Checking source content' },
  { key: 'presentation.generate_outline', label: 'Drafting outline' },
  { key: 'presentation.generate_slides', label: 'Creating slides' },
  { key: 'presentation.finalize', label: 'Finishing presentation' }
] as const

export function isActivePresentationBuildRun(run: AgentRun): boolean {
  return run.status === 'queued' || run.status === 'running' || run.status === 'waiting_for_input'
}

function presentationBuildStageLabel(run: AgentRun): string {
  if (run.status === 'waiting_for_input') return 'Waiting for input'
  return presentationBuildStages.find((stage) => stage.key === run.currentStage)?.label ?? 'Building presentation'
}

export function PresentationBuildProgress({
  run,
  starting,
  stopping,
  retrying,
  retryDisabled,
  onStop,
  onRetry
}: {
  run: AgentRun | null
  starting: boolean
  stopping: boolean
  retrying: boolean
  retryDisabled: boolean
  onStop: () => void
  onRetry: () => void
}) {
  if (run === null) {
    return (
      <section aria-live="polite" aria-label="Presentation build progress" className="shrink-0">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3 text-sm">
            <Loader2 aria-hidden="true" className="size-4 animate-spin text-primary motion-reduce:animate-none" />
            <span className="font-medium">{starting ? 'Starting presentation build...' : 'Preparing build...'}</span>
          </div>
        </div>
      </section>
    )
  }

  const active = isActivePresentationBuildRun(run)
  const progressLabel = run.totalStepsFinal
    ? `${run.completedSteps} of ${run.totalSteps ?? 0} steps complete`
    : 'Preparing the build plan'

  return (
    <section
      aria-labelledby="presentation-build-progress-title"
      aria-live="polite"
      className="max-h-[min(20rem,40vh)] shrink-0 overflow-y-auto"
    >
      <div className="rounded-lg border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 id="presentation-build-progress-title" className="text-lg font-semibold">
              {run.status === 'failed' ? 'Build needs attention' : 'Building presentation'}
            </h2>
            <p className="mt-1 text-sm font-medium text-foreground">{presentationBuildStageLabel(run)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{progressLabel}</p>
          </div>
          {active ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onStop}
              disabled={stopping || run.cancelRequested}
              aria-label={stopping || run.cancelRequested ? 'Stopping build' : 'Stop build'}
              className="shrink-0"
            >
              {stopping || run.cancelRequested ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
              ) : null}
              {stopping || run.cancelRequested ? 'Stopping...' : 'Stop'}
            </Button>
          ) : run.status === 'failed' ? (
            <Button type="button" size="sm" onClick={onRetry} disabled={retryDisabled} aria-label="Retry build">
              <RotateCcw
                aria-hidden="true"
                className={retrying ? 'size-4 animate-spin motion-reduce:animate-none' : 'size-4'}
              />
              {retrying ? 'Retrying...' : 'Retry'}
            </Button>
          ) : null}
        </div>

        {run.status === 'failed' && run.failureMessage && (
          <div
            role="alert"
            className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {run.failureMessage}
          </div>
        )}

        {run.steps.length > 0 ? (
          <ol className="mt-5 space-y-1" aria-label="Presentation build stages">
            {presentationBuildStages.map((stage) => {
              const step = run.steps.find((candidate) => candidate.kind === stage.key)
              const status = step?.status ?? (run.currentStage === stage.key ? run.status : 'pending')
              const current =
                status === 'running' ||
                status === 'waiting_for_input' ||
                (status === 'pending' && run.currentStage === stage.key)
              const failed = status === 'failed' || (run.status === 'failed' && run.currentStage === stage.key)
              return (
                <li key={stage.key} className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-3 py-2 text-sm">
                  {status === 'succeeded' ? (
                    <span className="flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check aria-hidden="true" className="size-3.5" />
                    </span>
                  ) : current && run.status !== 'failed' ? (
                    <Loader2
                      aria-hidden="true"
                      className="size-5 animate-spin text-primary motion-reduce:animate-none"
                    />
                  ) : failed ? (
                    <AlertTriangle aria-hidden="true" className="size-5 text-destructive" />
                  ) : (
                    <Circle aria-hidden="true" className="size-5 text-muted-foreground/50" />
                  )}
                  <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className={current ? 'font-medium text-foreground' : 'text-foreground'}>{stage.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {status === 'succeeded'
                        ? 'Complete'
                        : current && run.status !== 'failed'
                          ? 'In progress'
                          : failed
                            ? 'Needs attention'
                            : 'Waiting'}
                    </span>
                  </div>
                </li>
              )
            })}
          </ol>
        ) : run.status === 'failed' ? (
          <div className="mt-5 flex items-start gap-3 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-destructive" />
            No stage progress is available because the build stopped during this stage.
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-3 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
            Build stages will appear here as the presentation is prepared.
          </div>
        )}
      </div>
    </section>
  )
}
